import moment, { MomentInput } from 'moment'
import type { LicenseAndPaymentData } from '@/api/types/licenseAndPayment.types'

export type LicensePaymentStatus = 'normal' | 'expiresSoon' | 'expired'

type LicenseNoticeData = Pick<
  LicenseAndPaymentData,
  | 'expiration_date'
  | 'is_trial'
  | 'has_payments'
  | 'is_free'
  | 'payment_modal_enabled'
  | 'payment_reminder_enabled'
  | 'payment_grace_days'
>

const paymentGraceDays = (days: number | undefined) =>
  typeof days === 'number' && Number.isInteger(days) && days >= 0 && days <= 365
    ? days
    : 7

const parseDate = (value: MomentInput) =>
  typeof value === 'string'
    ? moment(value, [moment.ISO_8601, 'YYYY-MM-DD HH:mm:ss'], true)
    : moment(value)

export const getLicensePaymentStatus = (
  expirationDate: MomentInput | null | undefined,
  now: MomentInput = moment(),
): LicensePaymentStatus => {
  if (!expirationDate) return 'normal'

  const currentDate = parseDate(now)
  const targetDate = parseDate(expirationDate)
  if (!currentDate.isValid() || !targetDate.isValid()) return 'normal'

  if (currentDate.isAfter(targetDate)) return 'expired'

  return targetDate.diff(currentDate, 'days') <= 7 ? 'expiresSoon' : 'normal'
}

export const isLicenseOverdueByMoreThanDays = (
  expirationDate: MomentInput | null | undefined,
  days: number,
  now: MomentInput = moment(),
): boolean => {
  if (!expirationDate) return false

  const currentDate = parseDate(now)
  const targetDate = parseDate(expirationDate)
  if (!currentDate.isValid() || !targetDate.isValid()) return false

  return currentDate.isAfter(targetDate.add(days, 'days'))
}

export const isLicensePaymentModalDue = (
  data: LicenseNoticeData | undefined,
  now: MomentInput = moment(),
): boolean => {
  if (!data || data.payment_modal_enabled === false) return false
  if (!data.is_trial && !data.has_payments && data.is_free) return false

  return isLicenseOverdueByMoreThanDays(
    data.expiration_date,
    paymentGraceDays(data.payment_grace_days),
    now,
  )
}

export const isLicensePaymentReminderDue = (
  data: LicenseNoticeData | undefined,
  now: MomentInput = moment(),
): boolean => {
  // An older API does not opt users into the new reminder without its setting.
  if (!data || data.payment_reminder_enabled !== true || data.is_trial)
    return false
  if (!data.has_payments && data.is_free) return false
  return isLicenseOverdueByMoreThanDays(data.expiration_date, 0, now)
}

export const getNextLicensePaymentCheckTime = (
  data: LicenseNoticeData | undefined,
  now: number,
): number | null => {
  if (!data?.expiration_date) return null
  const expiration = parseDate(data.expiration_date)
  if (!expiration.isValid()) return null

  const deadlines: number[] = []
  if (data.payment_reminder_enabled === true && !data.is_trial) {
    deadlines.push(expiration.valueOf() + 1)
  }
  if (data.payment_modal_enabled !== false) {
    deadlines.push(
      expiration
        .clone()
        .add(paymentGraceDays(data.payment_grace_days), 'days')
        .valueOf() + 1,
    )
  }
  const upcoming = deadlines.filter((deadline) => deadline > now)
  return upcoming.length ? Math.min(...upcoming) : null
}

export const getDaysUntilLicenseClosing = (
  closingDate: MomentInput | null | undefined,
  now: MomentInput = moment(),
): number | null => {
  if (!closingDate) return null

  const currentDate = parseDate(now)
  const targetDate = parseDate(closingDate)
  if (!currentDate.isValid() || !targetDate.isValid()) return null

  return targetDate.startOf('day').diff(currentDate.startOf('day'), 'days')
}
